<template>
  <AuthLayout :mode="mode">
    <Section
      heading="Manage Manufacturers"
      description="<a href='/members/manufacturers/add' class='text-primary-100 font-semibold underline'>Add new manufacturer</a>"
      :mode="mode"
    />
    <AppCard :mode="mode">
      <template #body>
        <div
          class="mb-6 flex items-center justify-between gap-3 sm:flex-col sm:gap-4"
        >
          <div class="flex-1 sm:w-full">
            <div class="relative flex items-center">
              <Search
                :size="20"
                :stroke-width="1.75"
                class="absolute left-4 opacity-40"
              />
              <AppInput
                :mode="mode"
                type="search"
                placeholder="Search by manufacturer"
                v-model="mfrSearch.company"
                classes="!pl-11"
                :hasLabel="false"
              />
            </div>
          </div>
          <div class="w-[200px] sm:w-full">
            <AppButton
              v-if="mfrSearch.company"
              title="Search"
              @action="search"
            />
            <AppButton v-else title="Search" @action="getMfrs" />
          </div>
        </div>
        <template v-if="paginatedItems.length > 0">
          <div class="mb-4 hidden xl:block">
            <AppSelect
              :mode="mode"
              title="Sort by"
              :modelValue="mobileSort"
              @update:modelValue="applyMobileSort"
            >
              <template #options>
                <option value="">Default (best match)</option>
                <option
                  v-for="option in sortableColumns"
                  :key="`${option.sortKey}:asc`"
                  :value="`${option.sortKey}:asc`"
                >
                  {{ option.name }} (A-Z)
                </option>
                <option
                  v-for="option in sortableColumns"
                  :key="`${option.sortKey}:desc`"
                  :value="`${option.sortKey}:desc`"
                >
                  {{ option.name }} (Z-A)
                </option>
              </template>
            </AppSelect>
          </div>
          <AppTable
            :mode="mode"
            :columns="columns"
            :sortKey="sort.key"
            :sortOrder="sort.order"
            @sort="toggleSort"
          >
            <template #rows>
              <tr v-for="(mfr, key) in paginatedItems" :key="key">
                <td>
                  <FallbackText :value="mfr.mfr.mfr" />
                </td>
                <td>
                  <FallbackText :value="mfr.mfr.company" />
                </td>
                <td>
                  <Country
                    :mode="mode"
                    :country="mfr.mfr.country"
                    :countries="countries"
                  />
                </td>
                <td>
                  <a
                    :href="
                      mfr.mfr.url.includes('http')
                        ? mfr.mfr.url
                        : 'https://' + mfr.mfr.url
                    "
                    target="_blank"
                    v-if="mfr.mfr.url"
                    class="text-primary-100"
                  >
                    <span
                      class="block max-w-[180px] truncate"
                      v-tooltip="`${mfr.mfr.url}`"
                      >{{ mfr.mfr.url }}</span
                    >
                  </a>
                  <span v-else>-</span>
                </td>
                <td>
                  <div class="flex items-center gap-2">
                    <TableAction
                      :mode="mode"
                      @itemAction="
                        $redirectTo('edit-manufacturer', mfr.mfr.mfr_id)
                      "
                      action="edit"
                    />
                    <TableAction
                      :mode="mode"
                      @itemAction="confirmDelete(mfr.mfr.mfr_id)"
                      action="delete"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </AppTable>
          <MobileTable
            :mode="mode"
            :fields="{
              name: 'Name',
              company: 'Company',
              country: 'Country',
              url: 'URL',
              actions: 'Actions',
            }"
            :data="paginatedItems"
          >
            <template #default="{ row, fieldKey }">
              <template v-if="fieldKey === 'name'">
                <FallbackText :value="row.mfr.mfr" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'company'">
                <FallbackText :value="row.mfr.company" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'country'">
                <Country
                  :mode="mode"
                  :country="row.mfr.country"
                  :countries="countries"
                />
              </template>
              <template v-else-if="fieldKey === 'url'">
                <a
                  :href="
                    row.mfr.url.includes('http')
                      ? row.mfr.url
                      : 'https://' + row.mfr.url
                  "
                  target="_blank"
                  v-if="row.mfr.url"
                  class="text-primary-100"
                >
                  <span class="block max-w-[180px] truncate">{{
                    row.mfr.url
                  }}</span>
                </a>
                <span v-else>-</span>
              </template>
              <template v-else-if="fieldKey === 'actions'">
                <div class="flex items-center gap-2">
                  <TableAction
                    :mode="mode"
                    @itemAction="
                      $redirectTo('edit-manufacturer', row.mfr.mfr_id)
                    "
                    action="edit"
                  />
                  <TableAction
                    :mode="mode"
                    @itemAction="confirmDelete(row.mfr.mfr_id)"
                    action="delete"
                  />
                </div>
              </template>
            </template>
          </MobileTable>
        </template>
        <EmptyData
          v-else
          :notFound="'No manufacturers represented!'"
          :isClear="true"
          @clear="clear"
          :mode="mode"
        />
        <AppPagination
          :key="`${sort.key}:${sort.order}`"
          :mode="mode"
          :data="sortedItems"
          @pageChanged="updatePaginatedItems"
        />
      </template>
    </AppCard>
    <DeleteRecord
      :currentId="currentMfrId"
      :deleteType="'mfr'"
      :mode="mode"
      @recordDeleted="recordDeleted"
      @cancelDelete="currentMfrId = null"
    />
  </AuthLayout>
</template>

<script>
import { Search } from "lucide-vue-next";
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import tableSort from "@L/tableSort";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import EmptyData from "@C/Common/EmptyData.vue";
import DeleteRecord from "@C/DeleteRecord.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AppPagination from "@C/Common/AppPagination.vue";
import Country from "@C/Tables/Country.vue";
import AuthLayout from "@A/AuthLayout.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import AppButton from "@C/Common/AppButton.vue";
import TableAction from "@C/Tables/TableAction.vue";

export default {
  name: "ManageManufacturers",
  mixins: [tableSort],
  components: {
    AuthLayout,
    Section,
    EmptyData,
    DeleteRecord,
    AppCard,
    AppInput,
    AppSelect,
    AppTable,
    TableAction,
    Search,
    AppPagination,
    Country,
    FallbackText,
    MobileTable,
    AppButton,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const countriesStore = useCountriesStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      countriesStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      mfrs: [],
      mfrSearch: {
        company: "",
      },
      currentMfrId: null,
      paginatedItems: [],
      columns: [
        {
          name: "Name",
          sortKey: "mfr",
        },
        {
          name: "Company",
          sortKey: "company",
        },
        {
          name: "Country",
          sortKey: "country",
        },
        {
          name: "URL",
          sortKey: "url",
        },
        {
          name: "Actions",
          classes: "w-[100px]",
        },
      ],
    };
  },
  created() {
    if (!this.isAdmin) {
      this.toastStore.show(
        "You do not have permission to see this page",
        "error",
      );
      this.$router.push({ name: "home" });
      return;
    }
  },
  mounted() {
    this.getMfrs();
  },
  methods: {
    async search() {
      this.loaderStore.setIsLoading(true);
      const payload = this.mfrSearch;
      try {
        const { data } = await ApiRequest().post(`/mfr_search`, payload);
        if (data) {
          this.mfrs = data;
        }
      } catch (e) {
        this.handleError(e);
        this.mfrs = [];
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async getMfrs() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/mfr_summary`);
        if (data) {
          this.mfrs = data;
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    clear() {
      this.getMfrs();
      this.mfrSearch = {
        company: "",
      };
    },
    confirmDelete(id) {
      this.currentMfrId = id;
    },
    recordDeleted() {
      this.getMfrs();
      this.currentMfrId = null;
      this.toastStore.show("Manufacturer deleted successfully!", "success");
    },
    updatePaginatedItems(paginatedData) {
      this.paginatedItems = paginatedData;
    },
    sortValue(row, key) {
      const source = row.mfr;
      switch (key) {
        case "mfr":
          return source.mfr;
        case "company":
          return source.company;
        case "country":
          return source.country;
        case "url":
          return source.url;
        default:
          return "";
      }
    },
  },
  computed: {
    sortSource() {
      return this.mfrs;
    },
    countries() {
      return this.countriesStore.countries;
    },
    isAdmin() {
      return this.userStore.user.global_admin;
    },
  },
};
</script>
